import { Hourglass } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { OPEN_THREAD_STATE } from "@/features/agents/routes";
import type { FirmSkillRun } from "@/features/firmKnowledge/types";

import { THREAD_PARAM } from "../../chat/useThreadDeepLink";
import { caseWorkspacePath, parseCasePath } from "../../routes";
import { Chip } from "./chip";

const REFRESH_MS = 120_000;

/**
 * Automated skill runs on the case that wait on the attorney ("RFE response
 * due in 14 days · plan ready"). One run opens its thread; several open a
 * short list. Renders nothing when none wait or the call fails.
 */
export const AutomationChip: FC<{ individualId: string }> = ({
  individualId
}) => {
  const [runs, setRuns] = useState<FirmSkillRun[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      firmKnowledgeApi
        .listCaseAutomatedRuns(individualId)
        .then((rows) => {
          if (!cancelled) setRuns(rows);
        })
        .catch(() => {
          if (!cancelled) setRuns([]);
        });
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [individualId]);

  if (runs.length === 0) return null;

  const open = (run: FirmSkillRun) => {
    const ref = parseCasePath(location.pathname)?.ref;
    const base = ref ? caseWorkspacePath(ref) : location.pathname;
    navigate(`${base}?${THREAD_PARAM}=${encodeURIComponent(run.thread_id)}`, {
      state: OPEN_THREAD_STATE
    });
  };

  const [first] = runs;
  const label = first.chip ?? "Automation waiting";
  if (runs.length === 1) {
    return (
      <Chip
        onClick={() => open(first)}
        className="max-w-72 border-purple-line bg-purple-soft text-purple hover:bg-purple-soft"
        title={first.event?.headline ?? label}
        data-testid="case-automation-chip"
      >
        <Hourglass className="size-3.5 shrink-0 opacity-70" />
        <span className="truncate">{label}</span>
      </Chip>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Chip
          className="max-w-72 border-purple-line bg-purple-soft text-purple hover:bg-purple-soft"
          data-testid="case-automation-chip"
        >
          <Hourglass className="size-3.5 shrink-0 opacity-70" />
          <span className="truncate">{label}</span>
          <span className="shrink-0 font-medium">+{runs.length - 1}</span>
        </Chip>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-1">
        <ul className="flex flex-col">
          {runs.map((run) => (
            <li key={run.id}>
              <button
                type="button"
                className="w-full rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted"
                onClick={() => open(run)}
                data-testid="case-automation-run"
              >
                {run.chip ?? run.event?.headline ?? "Automated run"}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
